import React, { useState, useEffect } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Container,
    Paper,
    Typography,
    CircularProgress,
    TextField,
    FormControlLabel,
    Checkbox,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    Alert,
    useTheme,
    Stack,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    OutlinedInput,
    Chip,
    SelectChangeEvent
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { User, ApiRole, usersService } from './services/UsersService';
import { Account, accountsService } from '../services/AccountsService';
import { logger } from '../../../utils/logger';
import apiService from '../../../services/ApiService';
import Breadcrumb from '../../../components/Breadcrumb';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import EmailIcon from '@mui/icons-material/Email';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { environment } from '../../../environments/environment';
import { useAuth } from '../../../contexts/AuthContext';
import ROLES from '../../../constants/roles';

const log = logger.getLogger('EditUserPage');

const MIGRATE_ACCOUNT_SEARCH_DEBOUNCE_MS = 400;

interface FormState {
    firstName: string;
    lastName: string;
    username: string; // email
    roles: number[];
    enabled: boolean;
    locked: boolean;
    lastLoginDate: string;
    welcomeEmailDate: string;
}

const EditUserPage: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { auth } = useAuth();
    const { id, accountId } = useParams<{ id: string; accountId: string }>();
    const [user, setUser] = useState<User | null>(null);
    const [account, setAccount] = useState<Account | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [sendingEmail, setSendingEmail] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [emailSuccessMessage, setEmailSuccessMessage] = useState<string | null>(null);
    const [emailError, setEmailError] = useState<string | null>(null);

    // State for confirmation dialogs
    const [listenEmailDialogOpen, setListenEmailDialogOpen] = useState<boolean>(false);
    const [strategyEmailDialogOpen, setStrategyEmailDialogOpen] = useState<boolean>(false);
    const [reportingEmailDialogOpen, setReportingEmailDialogOpen] = useState<boolean>(false);

    // State for account migration
    const [migrateDialogOpen, setMigrateDialogOpen] = useState<boolean>(false);
    const [migrateConfirmDialogOpen, setMigrateConfirmDialogOpen] = useState<boolean>(false);
    const [selectedMigrateAccount, setSelectedMigrateAccount] = useState<Account | null>(null);
    const [migrating, setMigrating] = useState<boolean>(false);
    const [migrateAccounts, setMigrateAccounts] = useState<Account[]>([]);
    const [migrateAccountsLoading, setMigrateAccountsLoading] = useState<boolean>(false);
    const [migrateAccountSearch, setMigrateAccountSearch] = useState<string>('');

    const [formState, setFormState] = useState<FormState>({
        firstName: '',
        lastName: '',
        username: '', // email
        roles: [],
        enabled: true,
        locked: false,
        lastLoginDate: '',
        welcomeEmailDate: ''
    });

    // API-fetched roles
    const [availableRoles, setAvailableRoles] = useState<ApiRole[]>([]);

    const isEditMode = id && id !== 'create';
    const pageTitle = isEditMode ? `Edit User: ${user?.firstName} ${user?.lastName}` : 'Create User';

    const currentUserPrivs: string[] = (auth.user?.accountProfile?.privs as string[]) || [];
    const canMigrateUser = isEditMode &&
        currentUserPrivs.includes('VIEW_ACCOUNT_GLOBAL') &&
        currentUserPrivs.includes('UPDATE_USER_GLOBAL');

    // Fetch legacy and new roles from API
    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const roles = await usersService.getRoles();
                setAvailableRoles(roles);
            } catch (err) {
                log.error('Error fetching roles:', err);
            }
        };
        fetchRoles();
    }, []);

    useEffect(() => {
        if (isEditMode) {
            const fetchUserAndAccount = async () => {
                try {
                    setLoading(true);
                    setError(null);
                    
                    // Fetch user data and account data in parallel
                    const [userData, accountData] = await Promise.all([
                        usersService.getUser(Number(id)),
                        accountsService.getAccount(Number(accountId))
                    ]);
                    
                    setUser(userData);
                    setAccount(accountData);

                    // Initialize form state with user data
                    setFormState({
                        firstName: userData.firstName || '',
                        lastName: userData.lastName || '',
                        username: userData.username || '', // email
                        roles: [], // resolved to role IDs once availableRoles are loaded
                        enabled: userData.enabled !== false,
                        locked: userData.locked || false,
                        lastLoginDate: userData.lastLoginDate || '',
                        welcomeEmailDate: userData.welcomeEmailDate || ''
                    });

                    setLoading(false);
                } catch (err) {
                    log.error('Error fetching user or account:', err);
                    setError('Failed to load user details. Please try again later.');
                    setLoading(false);
                }
            };

            fetchUserAndAccount();
        } else {
            // Fetch account for breadcrumb in create mode
            accountsService.getAccount(Number(accountId))
                .then(setAccount)
                .catch(err => log.error('Error fetching account:', err))
                .finally(() => setLoading(false));
        }
    }, [id, accountId, isEditMode]);

    // In create mode, default the Roles field to Client User once roles are loaded
    useEffect(() => {
        if (!isEditMode && availableRoles.length > 0) {
            const clientUserRole = availableRoles.find(r => r.name === ROLES.ACCOUNT_USER.name);
            if (clientUserRole) {
                setFormState(prev => ({ ...prev, roles: [clientUserRole.id] }));
            }
        }
    }, [isEditMode, availableRoles]);

    // Resolve user role names to IDs once availableRoles are loaded
    useEffect(() => {
        if (user?.roles && availableRoles.length > 0) {
            const roleIds = user.roles
                .map(role => availableRoles.find(r => r.name === role.name)?.id)
                .filter((id): id is number => id !== undefined);
            setFormState(prev => ({ ...prev, roles: roleIds }));
        }
    }, [user, availableRoles]);

    const handleInputChange = (field: string, value: any) => {
        setFormState(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            setSaving(true);
            setError(null);

            const userData = {
                firstName: formState.firstName,
                lastName: formState.lastName,
                username: formState.username,
                roles: formState.roles,
                enabled: formState.enabled,
                locked: formState.locked
            };

            if (isEditMode && user) {
                await usersService.updateUser({
                    ...user,
                    ...userData,
                    roles: formState.roles as any // API expects number[] (role IDs) for POST/PUT
                });

                if (formState.enabled !== user.enabled) {
                    await usersService.changeUserStatus(user.id, formState.enabled);
                }
            } else {
                // Create new user
                await usersService.createUser({
                    ...userData,
                    accountId: Number(accountId) // Include accountId in payload for new users
                } as any);
            }

            setSuccessMessage(`User ${isEditMode ? 'updated' : 'created'} successfully!`);
            setTimeout(() => {
                navigate(`/accounts/${accountId}/users`);
            }, 1500);

        } catch (err) {
            log.error(`Error ${isEditMode ? 'updating' : 'creating'} user:`, err);
            setError(`Failed to ${isEditMode ? 'update' : 'create'} user. Please try again.`);
        } finally {
            setSaving(false);
        }
    };

    const handleBack = () => {
        navigate(`/accounts/${accountId}/users`);
    };

    const handleSendWelcomeEmail = async (portalType: 'listen' | 'strategy' | 'reporting') => {
        const portalDisplayName = portalType === 'listen' ? 'Listen Web' : 
        portalType === 'strategy' ? 'Strategy Edge' : 
        'Listen Reporting';
        try {
            setSendingEmail(true);
            setEmailError(null); // Clear previous email errors

            // Close the appropriate dialog
            if (portalType === 'listen') {
                setListenEmailDialogOpen(false);
            } else if (portalType === 'strategy') {
                setStrategyEmailDialogOpen(false);
            } else if (portalType === 'reporting') {
                setReportingEmailDialogOpen(false);
            }

            // Make API call to send welcome email
            const response = await apiService.makeRequest(
                'post',
                `${environment.services.account.baseUrl}${environment.services.account.basePath}/users/${id}/welcome-email`,
                { portalType }
            );


            if (response && response?.data && response.data?.welcomeEmailDate) {
                // Update user and form state with new welcomeEmailDate for all portal types
                setUser(prev => prev ? { ...prev, welcomeEmailDate: response.data?.welcomeEmailDate } : null);
                setFormState(prev => ({
                    ...prev,
                    welcomeEmailDate: response.data?.welcomeEmailDate
                }));
                
                setEmailSuccessMessage(`Welcome email for ${portalDisplayName} sent successfully!`);
            }
        } catch (err) {
            log.error(`Error sending welcome email (${portalType}):`, err);
            setEmailError(`Failed to send welcome email for ${portalDisplayName}. Please try again.`);
        } finally {
            setSendingEmail(false);
        }
    };

    // Fetch accounts for the migrate dialog, debounced on the typed search text
    useEffect(() => {
        if (!migrateDialogOpen) return;

        let cancelled = false;
        setMigrateAccountsLoading(true);
        const timeoutId = setTimeout(async () => {
            try {
                const result = await accountsService.getAccounts({
                    page: 0,
                    size: 500,
                    filters: { accountName: migrateAccountSearch }
                });
                if (!cancelled) setMigrateAccounts(result.data);
            } catch (err) {
                log.error('Error fetching accounts for migration:', err);
            } finally {
                if (!cancelled) setMigrateAccountsLoading(false);
            }
        }, MIGRATE_ACCOUNT_SEARCH_DEBOUNCE_MS);

        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
        };
    }, [migrateDialogOpen, migrateAccountSearch]);

    const handleOpenMigrateDialog = () => {
        setSelectedMigrateAccount(null);
        setMigrateAccountSearch('');
        setMigrateDialogOpen(true);
    };

    const handleMigrateAccountSelected = () => {
        setMigrateDialogOpen(false);
        setMigrateConfirmDialogOpen(true);
    };

    const handleConfirmMigrate = async () => {
        if (!selectedMigrateAccount || !id) return;
        try {
            setMigrating(true);
            await usersService.migrateUser(Number(id), selectedMigrateAccount.id);
            setMigrateConfirmDialogOpen(false);
            navigate(`/accounts/${selectedMigrateAccount.id}/users/${id}/edit`);
        } catch (err) {
            log.error('Error migrating user:', err);
            setError('Failed to migrate user. Please try again.');
            setMigrateConfirmDialogOpen(false);
        } finally {
            setMigrating(false);
        }
    };

    // Format date for display
    const formatDate = (dateString: string) => {
        if (!dateString) return 'Never';
        return new Date(dateString).toLocaleString();
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4 }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                    <CircularProgress size={60} thickness={4} color="primary" />
                </Box>
            </Container>
        );
    }

    if (error && !user && isEditMode) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4 }}>
                <Paper sx={{ p: 3 }}>
                    <Typography color="error" variant="h6">{error}</Typography>
                    <Button variant="contained" onClick={handleBack} sx={{ mt: 2 }}>
                        Back to Users
                    </Button>
                </Paper>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
            <Breadcrumb
                items={[
                    {
                        label: 'Accounts',
                        path: '/accounts',
                        icon: <BusinessIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                    },
                    {
                        label: account?.name || '',
                        path: `/accounts/${accountId}/edit`,
                        icon: <BusinessIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                    },
                    {
                        label: 'Users',
                        path: `/accounts/${accountId}/users`,
                        icon: <PersonIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                    },
                    {
                        label: isEditMode ? `Edit User` : 'Create User',
                    }
                ]}
            />

            <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                    {pageTitle}
                </Typography>

                {successMessage && (
                    <Alert severity="success" sx={{ mb: 3 }}>
                        {successMessage}
                    </Alert>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                <form onSubmit={handleSubmit}>
                    <Stack spacing={4}>
                        <Box>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                                User Information
                            </Typography>
                            <Divider sx={{ mb: 3, width: '100%' }} />

                            <Stack spacing={3}>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField
                                        fullWidth
                                        required
                                        id="firstName"
                                        label="First Name"
                                        value={formState.firstName}
                                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                                        variant="outlined"
                                    />

                                    <TextField
                                        fullWidth
                                        required
                                        id="lastName"
                                        label="Last Name"
                                        value={formState.lastName}
                                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                                        variant="outlined"
                                    />
                                </Box>

                                <Box>
                                    <TextField
                                        fullWidth
                                        required
                                        id="username"
                                        type="email"
                                        label="Email"
                                        value={formState.username}
                                        onChange={(e) => handleInputChange('username', e.target.value)}
                                        variant="outlined"
                                    />
                                </Box>

                                <Box>
                                    <FormControl fullWidth>
                                        <InputLabel id="roles-label">Roles</InputLabel>
                                        <Select
                                            labelId="roles-label"
                                            id="roles"
                                            multiple
                                            value={formState.roles}
                                            onChange={(e: SelectChangeEvent<number[]>) => {
                                                handleInputChange('roles', e.target.value as number[]);
                                            }}
                                            input={<OutlinedInput label="Roles" />}
                                            renderValue={(selected) => (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {selected.map((roleId) => {
                                                        const role = availableRoles.find(r => r.id === roleId);
                                                        return (
                                                            <Chip
                                                                key={roleId}
                                                                label={role?.displayName ?? roleId}
                                                                size="small"
                                                            />
                                                        );
                                                    })}
                                                </Box>
                                            )}
                                        >
                                            {availableRoles.map(role => (
                                                <MenuItem key={role.id} value={role.id}>{role.displayName}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                            </Stack>
                        </Box>

                        <Box>
                            <Typography variant="h6" sx={{ mt: 2, mb: 2, fontWeight: 500 }}>
                                Account Status
                            </Typography>
                            <Divider sx={{ mb: 3, width: '100%' }} />

                            <Stack spacing={2}>
                                <Box>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={formState.enabled}
                                                onChange={(e) => handleInputChange('enabled', e.target.checked)}
                                            />
                                        }
                                        label="Enabled"
                                    />
                                </Box>

                                <Box>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={formState.locked}
                                                onChange={(e) => handleInputChange('locked', e.target.checked)}
                                            />
                                        }
                                        label="Locked"
                                    />
                                </Box>
                            </Stack>
                        </Box>

                        {isEditMode && (
                            <Box>
                                <Typography variant="h6" sx={{ mt: 2, mb: 2, fontWeight: 500 }}>
                                    User Activity
                                </Typography>
                                <Divider sx={{ mb: 3, width: '100%' }} />

                                {emailSuccessMessage && (
                                    <Alert severity="success" sx={{ mb: 3 }}>
                                        {emailSuccessMessage}
                                    </Alert>
                                )}

                                {emailError && (
                                    <Alert severity="error" sx={{ mb: 3 }}>
                                        {emailError}
                                    </Alert>
                                )}

                                <Stack spacing={3}>
                                    <Box>
                                        <Typography variant="body1" fontWeight="medium">
                                            Last Login:
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {formatDate(formState.lastLoginDate)}
                                        </Typography>
                                    </Box>

                                    <Box>
                                        <Typography variant="body1" fontWeight="medium">
                                            Welcome Email Last Sent:
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            {formatDate(formState.welcomeEmailDate)}
                                        </Typography>

                                        <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                startIcon={<EmailIcon />}
                                                onClick={() => setListenEmailDialogOpen(true)}
                                                disabled={sendingEmail}
                                            >
                                                {sendingEmail ? 'Sending...' : 'Send Listen Web Welcome Email'}
                                            </Button>

                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                startIcon={<EmailIcon />}
                                                onClick={() => setStrategyEmailDialogOpen(true)}
                                                disabled={sendingEmail}
                                            >
                                                {sendingEmail ? 'Sending...' : 'Send Strategy Edge Welcome Email'}
                                            </Button>

                                            {/* Listen Reporting Welcome Email Button - Show in local environment for debugging, or if account has listenReportingOnly enabled */}
                                            {(environment.environmentName === 'development' || (account?.listenReportingOnly && account?.strategyPortalEnabled)) && (
                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    startIcon={<EmailIcon />}
                                                    onClick={() => setReportingEmailDialogOpen(true)}
                                                    disabled={sendingEmail}
                                                >
                                                    {sendingEmail ? 'Sending...' : 'Send Listen Reporting Welcome Email'}
                                                </Button>
                                            )}
                                        </Box>
                                    </Box>
                                </Stack>
                            </Box>
                        )}

                        <Box sx={{ mt: 2 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Box>
                                    {canMigrateUser && (
                                        <Button
                                            variant="outlined"
                                            color="warning"
                                            onClick={handleOpenMigrateDialog}
                                            startIcon={<SwapHorizIcon />}
                                            disabled={saving || migrating}
                                            sx={{
                                                px: 3,
                                                py: 1,
                                                borderRadius: 2
                                            }}
                                        >
                                            Migrate to Account
                                        </Button>
                                    )}
                                </Box>
                                <Box display="flex" gap={2}>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        onClick={handleBack}
                                        startIcon={<CancelIcon />}
                                        disabled={saving}
                                        sx={{
                                            px: 3,
                                            py: 1,
                                            borderRadius: 2
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                        disabled={
                                            saving ||
                                            !formState.firstName ||
                                            !formState.lastName ||
                                            !formState.username
                                        }
                                        sx={{
                                            px: 3,
                                            py: 1,
                                            borderRadius: 2
                                        }}
                                    >
                                        {saving ? 'Saving...' : isEditMode ? 'Save Changes' : 'Create User'}
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    </Stack>
                </form>
            </Paper>

            {/* Step 1: Select account to migrate to */}
            <Dialog
                open={migrateDialogOpen}
                onClose={() => setMigrateDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Migrate User to Account</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Select the account to migrate {formState.firstName} {formState.lastName} to.
                    </DialogContentText>
                    <Autocomplete
                        options={migrateAccounts}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        // Filtering is done server-side via the search param
                        filterOptions={(options) => options}
                        loading={migrateAccountsLoading}
                        value={selectedMigrateAccount}
                        onChange={(_, newValue) => setSelectedMigrateAccount(newValue)}
                        onInputChange={(_, newInputValue, reason) => {
                            // Ignore 'reset' (fired when an option is selected) so picking doesn't trigger a new search
                            if (reason !== 'reset') setMigrateAccountSearch(newInputValue);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Account"
                                variant="outlined"
                                autoFocus
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {migrateAccountsLoading && <CircularProgress color="inherit" size={20} />}
                                            {params.InputProps.endAdornment}
                                        </>
                                    )
                                }}
                            />
                        )}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setMigrateDialogOpen(false); setSelectedMigrateAccount(null); }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleMigrateAccountSelected}
                        variant="contained"
                        color="primary"
                        disabled={!selectedMigrateAccount}
                    >
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Step 2: Confirm migration */}
            <Dialog
                open={migrateConfirmDialogOpen}
                onClose={() => setMigrateConfirmDialogOpen(false)}
            >
                <DialogTitle>Confirm Migration</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to migrate <strong>{formState.firstName} {formState.lastName}</strong> from <strong>{account?.name}</strong> to <strong>{selectedMigrateAccount?.name}</strong>? This will move the user to the new account immediately.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMigrateConfirmDialogOpen(false)} disabled={migrating}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmMigrate}
                        variant="contained"
                        color="warning"
                        startIcon={migrating ? <CircularProgress size={20} color="inherit" /> : <SwapHorizIcon />}
                        disabled={migrating}
                    >
                        {migrating ? 'Migrating...' : 'Confirm Migration'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirmation Dialog for Listen Web Welcome Email */}
            <Dialog
                open={listenEmailDialogOpen}
                onClose={() => setListenEmailDialogOpen(false)}
            >
                <DialogTitle>Send Welcome Email</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to send a Listen Web welcome email to {formState.firstName} {formState.lastName}?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setListenEmailDialogOpen(false)}>Cancel</Button>
                    <Button
                        onClick={() => handleSendWelcomeEmail('listen')}
                        variant="contained"
                        color="primary"
                        disabled={sendingEmail}
                    >
                        {sendingEmail ? 'Sending...' : 'Send Email'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirmation Dialog for Strategy Edge Welcome Email */}
            <Dialog
                open={strategyEmailDialogOpen}
                onClose={() => setStrategyEmailDialogOpen(false)}
            >
                <DialogTitle>Send Welcome Email</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to send a Strategy Edge welcome email to {formState.firstName} {formState.lastName}?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setStrategyEmailDialogOpen(false)}>Cancel</Button>
                    <Button
                        onClick={() => handleSendWelcomeEmail('strategy')}
                        variant="contained"
                        color="primary"
                        disabled={sendingEmail}
                    >
                        {sendingEmail ? 'Sending...' : 'Send Email'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirmation Dialog for Listen Reporting Welcome Email */}
            <Dialog
                open={reportingEmailDialogOpen}
                onClose={() => setReportingEmailDialogOpen(false)}
            >
                <DialogTitle>Send Welcome Email</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to send a Listen Reporting welcome email to {formState.firstName} {formState.lastName}?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReportingEmailDialogOpen(false)}>Cancel</Button>
                    <Button
                        onClick={() => handleSendWelcomeEmail('reporting')}
                        variant="contained"
                        color="primary"
                        disabled={sendingEmail}
                    >
                        {sendingEmail ? 'Sending...' : 'Send Email'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default EditUserPage;